
import { useState, useMemo, useEffect, createContext, useContext } from "react";
import { PartCategory, PartSubcategory, PartFilter } from "@/data/parts";
// import { checkSupabaseConnection } from "@/lib/supabase"; // Temporarily commented out
import { SortOption, PartsFilterState } from "./filters/filter-types";
import { usePartsData } from "./filters/use-parts-data";
import { useUserPreferences } from "./filters/use-user-preferences";
import { usePartsFilterUtils } from "./filters/use-parts-filter-utils";

// Create context to access filter state throughout the app
export const PartsFilterContext = createContext<PartsFilterState | undefined>(undefined);

// Main hook for managing filter state
export const usePartsFilterState = (): PartsFilterState => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<PartCategory | "all">("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState<PartSubcategory | "all">("all");
  const [activeFilters, setActiveFilters] = useState<PartFilter[]>([]); // Changed to empty by default
  const [sortBy, setSortBy] = useState<SortOption>("rating-high"); // Default to sorting by highest rating
  const [isSupabaseConnected, setIsSupabaseConnected] = useState<boolean | null>(false); // Set to false for now

  // Load user preferences
  const { 
    minStarRating, 
    setMinStarRating, 
    defaultPrinterId, 
    defaultPrinterName, 
    viewMode, 
    setViewMode,
    requiredFilters
  } = useUserPreferences();

  // Set initial filters from user preferences when available
  useEffect(() => {
    if (requiredFilters.length > 0) {
      setActiveFilters(requiredFilters);
    }
  }, [requiredFilters]);

  // Commented out Supabase connection check
  /*
  useEffect(() => {
    // Check Supabase connection on component mount
    const checkConnection = async () => {
      const isConnected = await checkSupabaseConnection();
      setIsSupabaseConnected(isConnected);
    };
    checkConnection();
  }, []);
  */

  // Get parts data
  const { allParts, isLoading } = usePartsData(isSupabaseConnected);
  
  // Get filter utilities
  const { getFilteredParts } = usePartsFilterUtils();

  // Toggle filter function
  const toggleFilter = (filter: PartFilter) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter(f => f !== filter));
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };

  // Filter parts based on selected filters and search query
  const filteredParts = useMemo(() => {
    return getFilteredParts(
      allParts,
      searchQuery,
      selectedCategory,
      selectedSubcategory,
      activeFilters,
      sortBy,
      minStarRating
    );
  }, [
    allParts,
    searchQuery, 
    selectedCategory, 
    selectedSubcategory, 
    activeFilters, 
    sortBy, 
    minStarRating,
    getFilteredParts
  ]);

  return {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    selectedSubcategory,
    setSelectedSubcategory,
    activeFilters,
    toggleFilter,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    isSupabaseConnected,
    isLoading,
    filteredParts,
    minStarRating,
    setMinStarRating,
    defaultPrinterId,
    defaultPrinterName
  };
};

// Consumer hook to use filter context
export const usePartsFilter = () => {
  const context = useContext(PartsFilterContext);
  if (context === undefined) {
    // Use default implementation if not in context
    return usePartsFilterState();
  }
  return context;
};
